import { useState, useEffect, useRef } from "react";
import { useTranslation } from '../i18n';
import { apiUrl } from "../api";
import "./RSVP.css";

function RSVP() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    attending: null,
    guestCount: 1,
    foodPreference: "",
    additionalGuests: {},
    message: "",
    songRequest: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Load saved data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem("rsvpData");
    if (savedData) {
      setFormData(JSON.parse(savedData));
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Save data to localStorage on change
  useEffect(() => {
    localStorage.setItem("rsvpData", JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const finalValue = type === "number" ? Number(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleAttendingChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      attending: value,
      // reset fields that are only relevant when attending
      guestCount: value ? prev.guestCount || 1 : 1,
      additionalGuests: value ? prev.additionalGuests : {},
      foodPreference: value ? prev.foodPreference : "",
    }));

    if (errors.attending) {
      setErrors((prev) => ({
        ...prev,
        attending: "",
      }));
    }
  };

  const handleGuestCountChange = (value) => {
    const numericValue = Number(value) || 1;
    setFormData((prev) => ({
      ...prev,
      guestCount: numericValue,
      additionalGuests: numericValue > 1 ? prev.additionalGuests : {},
    }));
    setIsOpen(false); // Close dropdown after selection
  };

  const handleAdditionalGuestChange = (guestIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      additionalGuests: {
        ...prev.additionalGuests,
        [guestIndex]: {
          ...prev.additionalGuests[guestIndex],
          [field]: value,
        },
      },
    }));
  };

  const handleGuestMealPreference = (guestIndex, value) => {
    if (guestIndex === 0) {
      // Main person's meal preference
      setFormData((prev) => ({
        ...prev,
        foodPreference: value,
      }));
    } else {
      // Additional guest's meal preference
      setFormData((prev) => ({
        ...prev,
        additionalGuests: {
          ...prev.additionalGuests,
          [guestIndex - 1]: {
            ...prev.additionalGuests[guestIndex - 1],
            foodPreference: value,
          },
        },
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim())
      newErrors.firstName = t('rsvp.errors.firstNameRequired');
    if (!formData.lastName.trim())
      newErrors.lastName = t('rsvp.errors.lastNameRequired');
    if (formData.attending === null) newErrors.attending = t('rsvp.errors.pleaseSelect');
    if (formData.attending && !formData.foodPreference)
      newErrors.foodPreference = t('rsvp.errors.pleaseChoose');

    // Validate additional guest names and meal preferences
    if (formData.attending && formData.guestCount > 1) {
      for (let i = 0; i < formData.guestCount - 1; i++) {
        const guest = formData.additionalGuests[i];
        if (!guest?.firstName?.trim()) {
          newErrors[`guest${i}FirstName`] = t('rsvp.errors.firstNameRequired');
        }
        if (!guest?.lastName?.trim()) {
          newErrors[`guest${i}LastName`] = t('rsvp.errors.lastNameRequired');
        }
        if (!guest?.foodPreference) {
          newErrors[`guest${i}FoodPreference`] = t('rsvp.errors.pleaseChoose');
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(apiUrl("/api/rsvp"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to submit RSVP");
      }

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        localStorage.removeItem("rsvpData");

        setTimeout(() => {
          const successSection = document.querySelector(".rsvp-success");
          if (successSection) {
            const viewportHeight = window.innerHeight;
            const sectionHeight = successSection.offsetHeight;
            const offset = (viewportHeight - sectionHeight) / 2;

            window.scrollTo({
              top: successSection.offsetTop - offset,
              behavior: "smooth",
            });
          }
        }, 100);
      } else {
        throw new Error(result.message || "Failed to submit RSVP");
      }
    } catch (error) {
      console.error("RSVP submission error:", error);
      setErrors({
        submit: error.message || t("rsvp.errors.submitError"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      attending: null,
      guestCount: 1,
      foodPreference: "",
      additionalGuests: {},
      message: "",
      songRequest: "",
    });
    setSubmitted(false);
    setErrors({});
    localStorage.removeItem("rsvpData");
  };

  if (submitted) {
    return (
      <section className="rsvp-section">
        <div className="rsvp-success">
          <div className="success-icon">✓</div>
          <h2 className="success-title">{t("rsvp.success.thanks")}</h2>
          <p className="success-message">{t("rsvp.success.message")}</p>
          <div className="success-details">
            <p>
              <strong>{t("rsvp.success.name")}:</strong> {formData.firstName} {formData.lastName}
            </p>
            <p>
              <strong>{t("rsvp.success.attendance")}:</strong> {formData.attending ? "Ja" : "Nein"}
            </p>
            {formData.attending && (
              <p>
                <strong>{t("rsvp.success.guests")}:</strong> {formData.guestCount}
              </p>
            )}
            {formData.attending && formData.foodPreference && (
              <p>
                <strong>{t("rsvp.mealPreferences")}:</strong>{" "}
                {t(`rsvp.options.meals.${formData.foodPreference}`)}
              </p>
            )}
          </div>

          <button
            type="button"
            className="rsvp-button secondary"
            onClick={resetForm}
          >
            {t("rsvp.buttons.another")}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="rsvp-section">
      <form className="rsvp-form" onSubmit={handleSubmit}>
        <h2 className="rsvp-title">{t("rsvp.title")}</h2>

        {/* Basic Information */}
        <div className="form-section">
          <h3 className="section-title">{t("rsvp.personalInfo")}</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t("rsvp.firstName")} *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder={t("rsvp.placeholders.firstName")}
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="lastName">{t("rsvp.lastName")} *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder={t("rsvp.placeholders.lastName")}
              />
              {errors.lastName && (
                <span className="error-message">{errors.lastName}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">{t("rsvp.email")}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={t("rsvp.placeholders.email")}
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">{t("rsvp.phone")}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={t("rsvp.placeholders.phone")}
              />
            </div>
          </div>
        </div>

        {/* Attendance */}
        <div className="form-section">
          <h3 className="section-title">{t("rsvp.attendance")}</h3>
          <div className="attendance-buttons">
            <button
              type="button"
              className={`attendance-btn ${
                formData.attending === true ? "active" : ""
              }`}
              onClick={() => handleAttendingChange(true)}
            >
              <span className="btn-icon">✓</span>
              {t('rsvp.buttons.attending')}
            </button>
            <button
              type="button"
              className={`attendance-btn ${
                formData.attending === false ? "active" : ""
              }`}
              onClick={() => handleAttendingChange(false)}
            >
              <span className="btn-icon">✗</span>
              {t('rsvp.buttons.notAttending')}
            </button>
          </div>
          {errors.attending && (
            <span className="error-message">{errors.attending}</span>
          )}
        </div>

        {/* Guest Count */}
        {formData.attending && (
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="guestCount">{t('rsvp.guestCount')}</label>
              <div className="custom-dropdown" ref={dropdownRef}>
                <div 
                  className={`dropdown-header ${isOpen ? 'open' : ''}`}
                  onClick={() => setIsOpen(!isOpen)}
                >
                  <span>{t(`rsvp.options.guests.${formData.guestCount}`)}</span>
                  <svg className="dropdown-arrow" width="12" height="8" viewBox="0 0 12 8">
                    <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {isOpen && (
                  <div className="dropdown-options">
                    <div 
                      className="dropdown-option"
                      onClick={() => handleGuestCountChange(1)}
                    >
                      {t('rsvp.options.guests.1')}
                    </div>
                    <div 
                      className="dropdown-option"
                      onClick={() => handleGuestCountChange(2)}
                    >
                      {t('rsvp.options.guests.2')}
                    </div>
                    <div 
                      className="dropdown-option"
                      onClick={() => handleGuestCountChange(3)}
                    >
                      {t('rsvp.options.guests.3')}
                    </div>
                    <div 
                      className="dropdown-option"
                      onClick={() => handleGuestCountChange(4)}
                    >
                      {t('rsvp.options.guests.4')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

          {/* Additional Guest Names */}
          {formData.attending && formData.guestCount > 1 && (
            <div className="form-section">
              <h3 className="section-title">{t('rsvp.additionalGuests')}</h3>
                {Array.from({ length: formData.guestCount - 1 }, (_, index) => (
                  <div key={index} className="form-row">
                    <div className="form-group">
                      <label htmlFor={`guest-${index}-firstName`}>
                        {t("rsvp.guest")} {index + 2} {t("rsvp.firstName")} *
                      </label>
                      <input
                        type="text"
                        id={`guest-${index}-firstName`}
                        value={
                          formData.additionalGuests[index]?.firstName || ""
                        }
                        onChange={(e) =>
                          handleAdditionalGuestChange(
                            index,
                            "firstName",
                            e.target.value
                          )
                        }
                        placeholder={t("rsvp.placeholders.firstName")}
                      />
                      {errors[`guest${index}FirstName`] && (
                        <span className="error-message">
                          {errors[`guest${index}FirstName`]}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor={`guest-${index}-lastName`}>
                        {t("rsvp.guest")} {index + 2} {t("rsvp.lastName")} *
                      </label>
                      <input
                        type="text"
                        id={`guest-${index}-lastName`}
                        value={formData.additionalGuests[index]?.lastName || ""}
                        onChange={(e) =>
                          handleAdditionalGuestChange(
                            index,
                            "lastName",
                            e.target.value
                          )
                        }
                        placeholder={t("rsvp.placeholders.lastName")}
                      />
                      {errors[`guest${index}LastName`] && (
                        <span className="error-message">
                          {errors[`guest${index}LastName`]}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

          {/* Food Preferences */}
          {formData.attending && (
            <div className="form-section">
              <h3 className="section-title">{t("rsvp.mealPreferences")}</h3>
              
              {/* Main person's meal preference */}
              <div className="form-group">
                <label>{formData.firstName} {formData.lastName} *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="foodPreference"
                      value="none"
                      checked={formData.foodPreference === "none"}
                      onChange={handleInputChange}
                    />
                    <span className="radio-custom"></span>
                    {t("rsvp.options.meals.none")}
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="foodPreference"
                      value="vegetarian"
                      checked={formData.foodPreference === "vegetarian"}
                      onChange={handleInputChange}
                    />
                    <span className="radio-custom"></span>
                    {t("rsvp.options.meals.vegetarian")}
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="foodPreference"
                      value="vegan"
                      checked={formData.foodPreference === "vegan"}
                      onChange={handleInputChange}
                    />
                    <span className="radio-custom"></span>
                    {t("rsvp.options.meals.vegan")}
                  </label>
                </div>
                {errors.foodPreference && (
                  <span className="error-message">{errors.foodPreference}</span>
                )}
              </div>

              {/* Additional guests' meal preferences */}
              {formData.guestCount > 1 && Array.from({ length: formData.guestCount - 1 }, (_, index) => {
                const guest = formData.additionalGuests[index];
                const guestName = guest?.firstName && guest?.lastName 
                  ? `${guest.firstName} ${guest.lastName}` 
                  : `${t("rsvp.guest")} ${index + 2}`;
                
                return (
                  <div key={index} className="form-group">
                    <label>{guestName} *</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name={`guest-food-${index}`}
                          value="none"
                          checked={guest?.foodPreference === "none"}
                          onChange={() => handleGuestMealPreference(index + 1, "none")}
                        />
                        <span className="radio-custom"></span>
                        {t("rsvp.options.meals.none")}
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name={`guest-food-${index}`}
                          value="vegetarian"
                          checked={guest?.foodPreference === "vegetarian"}
                          onChange={() => handleGuestMealPreference(index + 1, "vegetarian")}
                        />
                        <span className="radio-custom"></span>
                        {t("rsvp.options.meals.vegetarian")}
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name={`guest-food-${index}`}
                          value="vegan"
                          checked={guest?.foodPreference === "vegan"}
                          onChange={() => handleGuestMealPreference(index + 1, "vegan")}
                        />
                        <span className="radio-custom"></span>
                        {t("rsvp.options.meals.vegan")}
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        {/* Personal Message */}
        <div className="form-section">
          <h3 className="section-title">{t("rsvp.personalMessage")}</h3>
          <div className="form-group">
            <label htmlFor="message">{t("rsvp.message")}</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder={formData.guestCount > 1 ? t("rsvp.placeholders.messagePlural") : t("rsvp.placeholders.message")}
              rows="4"
            />
          </div>

          <div className="form-group">
            <label htmlFor="songRequest">
              {formData.guestCount > 1 ? t("rsvp.songRequestsPlural") : t("rsvp.songRequests")}
            </label>
            <input
              type="text"
              id="songRequest"
              name="songRequest"
              value={formData.songRequest}
              onChange={handleInputChange}
              placeholder={formData.guestCount > 1 ? t("rsvp.placeholders.songsPlural") : t("rsvp.placeholders.songs")}
            />
          </div>
        </div>

        {/* Submit Button */}
        {errors.submit && (
          <div className="error-message submit-error">{errors.submit}</div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="rsvp-button primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div>
                <span className="spinner"></span>
                {t("rsvp.buttons.submitting")}
              </div>
            ) : (
              t("rsvp.buttons.submit")
            )}
          </button>
        </div>
      </form>
    </section>
  );
}

export default RSVP;
